import type { ContractItem } from '@/data/schema/contract-item'
import { ApiService } from './api/api'
import type { StatusUrl } from '@/data/types/contract-status'
import type { StatusUpdate } from '@/data/interfaces/status-update'
import type { WorkspaceArea } from '@/data/types/area'
import { paramsToQueryString } from '@/utils/params'
import type { WorkspaceOptions } from '@/data/types/query-params'
import objectToQueryString from '@/utils/query-string'

export default class ContractService {
	private apiService

	constructor() {
		this.apiService = new ApiService(true)
	}

	/**
	 * Get a contract record by ID
	 * @param id string - contract number
	 * @param params Object
	 * @returns Promise
	 */
	get(id: string, params?: Object): Promise<any> {
		return this.apiService.get(`/contracts/${id}`, {
			params: params
		})
	}

	/**
	 * Get a contracts items by ID
	 * @param id string - contract number
	 * @param params Object
	 * @returns Promise
	 */
	getItems(id: string, params?: Object): Promise<any> {
		return this.apiService.get(`/contracts/${id}/items`, {
			params: params
		})
	}

	/**
	 * Get Hire Desk Contract Lists By Area
	 * @param area stock-check | pick | journey
	 * @param depot e.g. DEW|GAR
	 * @param page
	 * @param perPage
	 * @param sort
	 * @param options
	 * @param params
	 * @returns Promise
	 */
	getHireDeskArea(
		area: WorkspaceArea,
		depot?: string,
		page = 1,
		perPage: Number = 10,
		sort?: string | null,
		options?: WorkspaceOptions,
		params?: Object
	): Promise<any> {
		let url = objectToQueryString(
			`/service/hire-desk/contracts/${area}/${depot}?withContractStatus=true&page=${page}&per_page=${perPage}`,
			options
		)

		if (sort) {
			url += `&sort=${sort}`
		}

		return this.apiService.get(url, {
			params: params
		})
	}

	/**
	 * Get Yard Workspace Contract Lists By Area
	 * @param area stock-check | pick | journey
	 * @param depot e.g. DEW|GAR
	 * @param page
	 * @param perPage
	 * @param sort
	 * @param options
	 * @param params
	 * @returns Promise
	 */
	getYardWorkspaceArea(
		area: WorkspaceArea,
		depot?: string,
		page: Number = 1,
		perPage: Number = 10,
		sort?: string | null,
		options?: WorkspaceOptions,
		params?: Object
	): Promise<any> {
		let url = objectToQueryString(
			`/service/yard-workspace/contracts/${area}/${
				depot ?? ''
			}?withContractStatus=true&page=${page}&per_page=${perPage}`,
			options
		)

		if (sort) {
			url += `&sort=${sort}`
		}

		return this.apiService.get(url, {
			params: params
		})
	}

	/**
	 * Get Yard App contract lists by area
	 * @param operator User ID
	 * @param area stock-check | pick | journey
	 * @param depot e.g. DEW|GAR
	 * @param page
	 * @param perPage
	 * @param options
	 * @returns Promise
	 */
	getYardAppArea(
		operator: string,
		area: WorkspaceArea,
		depot?: string,
		page: Number = 1,
		perPage: Number = 10,
		options?: WorkspaceOptions
	): Promise<any> {
		let url = objectToQueryString(
			`/service/yard-app/contracts/${area}/${depot}?page=${page}&per_page=${perPage}`,
			options
		)

		if (area == 'journey' || area == 'load-delivery') {
			url += `&loadOperatorId=${operator}`
		} else {
			url += `&operatorId=${operator}`
		}

		return this.apiService.get(url)
	}

	/**
	 * Update contract status
	 * @param contract_number
	 * @param status_id
	 * @param params
	 * @returns Promise
	 */
	updateStatus(contract_number: string, status_id: string, params: Object): Promise<any> {
		return this.apiService.patch(`/contracts/${contract_number}/status/${status_id}`, params)
	}

	/**
	 * Update a Contract Item Status record
	 * @param item_id Contract Item ID
	 * @param status_id Contract Item Status ID
	 * @param url stock-status | pick-status | load-status
	 * @param params Object
	 * @param urlParams Object
	 * @returns
	 */
	updateItemStatus(
		item_id: string,
		status_id: number,
		url: StatusUrl,
		params: Object,
		urlParams?: Object
	): Promise<any> {
		const query = paramsToQueryString(urlParams)

		return this.apiService.patch(
			`/contract-items/${item_id}/${url}/${status_id}${query}`,
			params
		)
	}

	/**
	 * Bulk create Contract Item Status records
	 * @param items
	 * @param url stock-status | pick-status | load-status
	 * @returns Promise
	 */
	bulkCreateStatus(items: Array<StatusUpdate>, url: StatusUrl): Promise<any> {
		return this.apiService.post(`/contract-items/${url}/bulk-create`, {
			items: items
		})
	}

	/**
	 * Bulk update Contract Item Status records
	 * @param items
	 * @param url stock-status | pick-status | load-status
	 * @param save boolean
	 * @param confirmed boolean
	 * @returns Promise
	 */
	bulkUpdateStatus(
		items: Array<StatusUpdate>,
		url: StatusUrl,
		save = false,
		confirmed = false
	): Promise<any> {
		const params = save ? '?save=true' : ''

		return this.apiService.patch(`/contract-items/${url}/bulk-update${params}`, {
			items: items,
			quality_check_confirmed: confirmed ? true : undefined
		})
	}

	bulkUpdateDepots(items: Array<StatusUpdate>): Promise<any> {
		return this.apiService.patch(`/contract-items/bulk-update`, {
			items: items
		})
	}

	/**
	 * Get item stock for depot
	 * @param id string
	 * @param depot string
	 * @returns Promise
	 */
	getStock(id: string, depot?: string): Promise<any> {
		if (depot) {
			return this.apiService.get(`/stock/depot/${depot}/${id}`)
		}

		return this.apiService.get(`/stock/${id}`)
	}

	/**
	 * Get stock fulfilment data with depot distances
	 * @param postcode string
	 * @param items Object[]
	 * @returns Promise
	 */
	getStockFulfilment(postcode: string, items: Array<Object>): Promise<any> {
		return this.apiService.post(`/stock-fulfilment/${postcode}`, {
			items: items
		})
	}

	/**
	 * Send items to the defined depot yard
	 * Note: item.depot value is where it will be sent
	 * @param items
	 * @returns Promise
	 */
	sendItemsToYard(items: Array<ContractItem>): Promise<any> {
		return this.apiService.post('/contract-items/stock-status/bulk-create', {
			items: items
		})
	}
}
